/**
 * Petite couche promesse au-dessus d'IndexedDB, sans dependance.
 *
 * `factory` est l'IDBFactory a utiliser : celle d'un Storage Bucket
 * (bucket.indexedDB) ou celle par defaut (window.indexedDB). Voir storage.js.
 */

export class StorageError extends Error {
    constructor(message, code, cause) {
        super(message);
        this.name = 'StorageError';
        this.code = code;
        if (cause) this.cause = cause;
    }
}

/** IDBRequest -> Promise du resultat. */
export function requestToPromise(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** Promise resolue quand la transaction est validee, rejetee si elle echoue. */
export function transactionDone(tx) {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new StorageError('Transaction annulee', 'TX_ABORTED'));
    });
}

/**
 * Ouvre (ou cree) la base et joue les migrations manquantes, dans l'ordre.
 * Une version sans migration fait echouer l'ouverture : la base reste dans sa
 * version precedente, intacte.
 *
 * @param {object} options
 * @param {IDBFactory} options.factory
 * @param {string} options.name
 * @param {number} options.version
 * @param {Record<number, (db: IDBDatabase, tx: IDBTransaction) => void>} options.migrations
 * @param {() => void} [options.onVersionChange] appele quand un autre onglet
 *   demande une version plus recente : la base est alors fermee ici.
 * @returns {Promise<IDBDatabase>}
 */
export function openDatabase({ factory, name, version, migrations, onVersionChange }) {
    return new Promise((resolve, reject) => {
        let request;
        try {
            request = factory.open(name, version);
        } catch (error) {
            reject(new StorageError('Impossible d\'ouvrir le stockage du poste', 'OPEN_FAILED', error));
            return;
        }
        let migrationError = null;

        request.onupgradeneeded = (event) => {
            const db = request.result;
            const tx = request.transaction;
            const from = event.oldVersion || 0;
            const to = event.newVersion;
            try {
                for (let v = from + 1; v <= to; v++) {
                    const migrate = migrations[v];
                    if (typeof migrate !== 'function') {
                        throw new StorageError(`Migration manquante pour la version ${v}`, 'MIGRATION_MISSING');
                    }
                    migrate(db, tx);
                }
            } catch (error) {
                migrationError = error;
                tx.abort();
            }
        };
        request.onblocked = () => {
            // Un autre onglet garde l'ancienne version ouverte. La demande
            // reste en attente : elle aboutira quand il la fermera.
        };
        request.onsuccess = () => {
            const db = request.result;
            db.onversionchange = () => {
                db.close();
                if (onVersionChange) onVersionChange();
            };
            resolve(db);
        };
        request.onerror = () => {
            const cause = migrationError || request.error;
            reject(cause instanceof StorageError
                ? cause
                : new StorageError('Impossible d\'ouvrir le stockage du poste', 'OPEN_FAILED', cause));
        };
    });
}
